/**
 * The right-hand AI surface: an inline column on wide screens, a sheet on
 * everything narrower. Either way the contents are the same component, owned by
 * the AI feature — this file only decides where it lives and how it opens.
 *
 * It is violet-edged and labeled, because a model's answer must never be
 * mistaken for a computed figure.
 */
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { Maximize2Icon, Minimize2Icon, SparklesIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useMediaQuery } from '@/lib/motion'
import {
  AI_PANEL_MAX_WIDTH,
  AI_PANEL_MIN_WIDTH,
  clampWidth,
  useAiPanel,
} from './ai-panel'

const AiPanel = lazy(() =>
  import('@/features/ai/AiPanel').then((module) => ({ default: module.AiPanel })),
)

export function AiPanelContainer() {
  const { open, setOpen, width, setWidth, expanded, setExpanded } = useAiPanel()
  // Exactly one of the two renders. A sheet that is merely CSS-hidden still
  // traps focus and locks page scrolling, which would break the wide layout.
  const wide = useMediaQuery('(min-width: 1280px)')
  const panelRef = useRef<HTMLElement>(null)

  /*
   * The inline panel deliberately does NOT trap focus — it is a persistent
   * column you work alongside, not a modal, and trapping would make the rest
   * of the app unreachable while it is open.
   *
   * But it renders after <main> in the DOM, so opening it and pressing Tab
   * would walk the whole screen first. Moving focus to the panel on open makes
   * it reachable; the Ask button is still one Shift+Tab away, and closing it
   * hands focus back (see lib/return-focus.ts for the modal equivalent).
   */
  const wasOpen = useRef(false)
  useEffect(() => {
    if (!wide) {
      wasOpen.current = open
      return
    }
    if (open) {
      panelRef.current?.focus()
    } else if (wasOpen.current) {
      // Closing unmounts the panel, which would drop focus on <body>. Hand it
      // back to the control that opened it.
      const trigger = document.querySelector<HTMLElement>('[aria-controls="ai-panel"]')
      trigger?.focus()
    }
    wasOpen.current = open
  }, [open, wide])

  // Expanded is a reading mode, not a second panel: same component, same
  // conversation, more room. Escape leaves it, because a surface that covers
  // the app has to be dismissible without hunting for a control.
  useEffect(() => {
    if (!expanded || !open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expanded, open, setExpanded])

  if (wide) {
    if (!open) return null

    const header = (
      <div className="border-hairline flex h-12 shrink-0 items-center justify-between border-b px-3">
        <span className="font-display text-violet flex items-center gap-2 text-sm font-medium">
          <SparklesIcon className="size-4" aria-hidden />
          Ask
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setExpanded(!expanded)}
            aria-pressed={expanded}
            aria-label={expanded ? 'Leave full screen' : 'Expand to full screen'}
          >
            {expanded ? <Minimize2Icon aria-hidden /> : <Maximize2Icon aria-hidden />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setOpen(false)}
            aria-label="Close the AI panel"
          >
            <XIcon aria-hidden />
          </Button>
        </div>
      </div>
    )

    const body = (
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Suspense fallback={<PanelSkeleton />}>
          <AiPanel />
        </Suspense>
      </div>
    )

    if (expanded) {
      return (
        <aside
          ref={panelRef}
          tabIndex={-1}
          id="ai-panel"
          aria-label="Ask"
          className="bg-panel fixed inset-0 z-40 flex flex-col"
        >
          {header}
          {/* Wide screens make a full-bleed transcript hard to read, so the
              content keeps a measure even when the surface does not. */}
          <div className="mx-auto flex w-full max-w-4xl min-h-0 flex-1 flex-col">{body}</div>
        </aside>
      )
    }

    return (
      <aside
        ref={panelRef}
        tabIndex={-1}
        id="ai-panel"
        aria-label="Ask"
        style={{ width }}
        className="border-hairline bg-panel sticky top-14 flex h-[calc(100dvh-3.5rem)] shrink-0 flex-col border-l"
      >
        <ResizeHandle width={width} onResize={setWidth} />
        {header}
        {body}
      </aside>
    )
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" id="ai-panel" className="w-full gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-hairline border-b">
          <SheetTitle className="font-display text-violet flex items-center gap-2 text-sm">
            <SparklesIcon className="size-4" aria-hidden />
            Ask
          </SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Suspense fallback={<PanelSkeleton />}>{open && <AiPanel />}</Suspense>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * The left edge, as a drag target.
 *
 * `role="separator"` with the arrow keys is not decoration: a pointer-only
 * resize is unusable without a mouse, and this control decides how much of the
 * screen the panel takes. Pointer capture keeps the drag alive when the cursor
 * outruns the handle, which at 4px wide it always does.
 */
function ResizeHandle({ width, onResize }: { width: number; onResize: (width: number) => void }) {
  const start = useRef<{ x: number; width: number } | null>(null)

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      start.current = { x: event.clientX, width }
    },
    [width],
  )

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!start.current) return
      // Dragging left widens: the panel is anchored to the right edge.
      onResize(clampWidth(start.current.width + (start.current.x - event.clientX)))
    },
    [onResize],
  )

  const onPointerUp = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    start.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }, [])

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 64 : 16
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        onResize(clampWidth(width + step))
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        onResize(clampWidth(width - step))
      }
    },
    [onResize, width],
  )

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the AI panel"
      aria-valuenow={width}
      aria-valuemin={AI_PANEL_MIN_WIDTH}
      aria-valuemax={AI_PANEL_MAX_WIDTH}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onKeyDown={onKeyDown}
      className="hover:bg-violet/40 focus-visible:bg-violet/60 focus-visible:outline-ring absolute top-0 left-0 z-30 h-full w-1 cursor-col-resize touch-none transition-colors focus-visible:outline-2"
    />
  )
}

function PanelSkeleton() {
  return (
    <div className="space-y-3 p-4">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-4 w-24" />
    </div>
  )
}
